/**
 * Responsibility: Implement gdrive sync behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import { getAppImportMetaEnv } from "@anyquest/shared-core"
const GSI_SRC = 'https://accounts.google.com/gsi/client'
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.appdata'
const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3'
const DRIVE_UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3'
const appEnv = getAppImportMetaEnv()
const CLIENT_ID = String(appEnv.VITE_GOOGLE_OAUTH_CLIENT_ID ?? '').trim()

const FILE_PREFIX = 'am2-save-'
const FILE_SUFFIX = '.json'

type TokenResponse = {
  access_token?: string
  expires_in?: number
  error?: string
  error_description?: string
}

type TokenClient = {
  callback: (resp: TokenResponse) => void
  requestAccessToken: (options?: { prompt?: string }) => void
}

type GoogleIdentityWindow = Window & {
  google?: {
    accounts?: {
      oauth2?: {
        initTokenClient: (config: {
          client_id: string
          scope: string
          callback: (resp: TokenResponse) => void
        }) => TokenClient
      }
    }
  }
}

export type DriveBackupFile = {
  id: string
  name: string
  modifiedTime: string
  size: number
}

let scriptPromise: Promise<void> | null = null
let tokenClient: TokenClient | null = null
let accessToken = ''
let tokenExpiresAt = 0

function assertConfigured(): void {
  if (!CLIENT_ID) {
    throw new Error('VITE_GOOGLE_OAUTH_CLIENT_ID is not configured')
  }
}

function now(): number {
  return Date.now()
}

function hasValidToken(): boolean {
  return !!accessToken && tokenExpiresAt - 30_000 > now()
}

function trimTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '')
}

async function ensureGsiLoaded(): Promise<void> {
  assertConfigured()
  if (typeof window === 'undefined') throw new Error('Drive sync requires a browser')
  const browserWindow = window as GoogleIdentityWindow
  if (browserWindow.google?.accounts?.oauth2) return
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`)
    if (existing && browserWindow.google?.accounts?.oauth2) {
      resolve()
      return
    }

    const script = existing ?? document.createElement('script')
    script.src = GSI_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Google Identity Services script'))
    if (!existing) document.head.appendChild(script)
  })

  return scriptPromise
}

async function ensureTokenClient(): Promise<TokenClient> {
  await ensureGsiLoaded()
  const browserWindow = window as GoogleIdentityWindow
  if (!browserWindow.google?.accounts?.oauth2) {
    throw new Error('Google Identity Services unavailable')
  }
  if (tokenClient) return tokenClient

  tokenClient = browserWindow.google.accounts.oauth2.initTokenClient({
    client_id: CLIENT_ID,
    scope: DRIVE_SCOPE,
    callback: () => {},
  })
  return tokenClient as TokenClient
}

async function requestAccessToken(interactive: boolean): Promise<string> {
  if (hasValidToken()) return accessToken
  const client = await ensureTokenClient()

  return new Promise((resolve, reject) => {
    client.callback = (resp: TokenResponse) => {
      if (resp.error || !resp.access_token) {
        reject(new Error(resp.error_description || resp.error || 'Drive auth failed'))
        return
      }
      accessToken = resp.access_token
      const ttlMs = Math.max(60, Number(resp.expires_in || 3600)) * 1000
      tokenExpiresAt = now() + ttlMs
      resolve(accessToken)
    }

    client.requestAccessToken({
      prompt: interactive ? 'consent' : '',
    })
  })
}

async function driveFetch(url: string, init: RequestInit = {}, retry = true): Promise<Response> {
  const token = await requestAccessToken(false)
  const headers = new Headers(init.headers || {})
  headers.set('Authorization', `Bearer ${token}`)
  const response = await fetch(url, { ...init, headers })

  if (response.status === 401 && retry) {
    disconnectDrive()
    const nextToken = await requestAccessToken(false)
    const retriedHeaders = new Headers(init.headers || {})
    retriedHeaders.set('Authorization', `Bearer ${nextToken}`)
    return fetch(url, { ...init, headers: retriedHeaders })
  }

  return response
}

function toBackupFile(name: string): string {
  const safe = name.replace(/[^\w.-]+/g, '-')
  return `${FILE_PREFIX}${safe}${FILE_SUFFIX}`
}

async function listByName(name: string): Promise<DriveBackupFile[]> {
  const q = encodeURIComponent(`name='${name}' and trashed=false`)
  const fields = encodeURIComponent('files(id,name,modifiedTime,size)')
  const url = `${DRIVE_API_BASE}/files?spaces=appDataFolder&q=${q}&fields=${fields}&pageSize=5&orderBy=modifiedTime desc`
  const response = await driveFetch(url)
  if (!response.ok) {
    throw new Error(`Drive list failed (${response.status})`)
  }
  const payload = await response.json() as { files?: Array<Record<string, unknown>> }
  return (payload.files || []).map(file => ({
    id: String(file.id || ''),
    name: String(file.name || ''),
    modifiedTime: String(file.modifiedTime || ''),
    size: Number(file.size || 0),
  })).filter(file => file.id)
}

function multipartBody(metadata: Record<string, unknown>, raw: string): { boundary: string; body: string } {
  const boundary = `am2_${Math.random().toString(36).slice(2)}`
  const body = [
    `--${boundary}`,
    'Content-Type: application/json; charset=UTF-8',
    '',
    JSON.stringify(metadata),
    `--${boundary}`,
    'Content-Type: application/json',
    '',
    raw,
    `--${boundary}--`,
  ].join('\r\n')
  return { boundary, body }
}

export function isDriveSyncConfigured(): boolean {
  return !!CLIENT_ID
}

export function isDriveConnected(): boolean {
  return hasValidToken()
}

export async function connectDrive(): Promise<void> {
  await requestAccessToken(true)
}

export function disconnectDrive(): void {
  accessToken = ''
  tokenExpiresAt = 0
}

export async function listDriveBackups(limit = 10): Promise<DriveBackupFile[]> {
  assertConfigured()
  const q = encodeURIComponent(`name contains '${FILE_PREFIX}' and name contains '${FILE_SUFFIX}' and trashed=false`)
  const fields = encodeURIComponent('files(id,name,modifiedTime,size)')
  const safeLimit = Math.min(50, Math.max(1, Math.round(limit)))
  const url = `${DRIVE_API_BASE}/files?spaces=appDataFolder&q=${q}&fields=${fields}&pageSize=${safeLimit}&orderBy=modifiedTime desc`
  const response = await driveFetch(url)
  if (!response.ok) {
    throw new Error(`Drive list failed (${response.status})`)
  }
  const payload = await response.json() as { files?: Array<Record<string, unknown>> }
  return (payload.files || []).map(file => ({
    id: String(file.id || ''),
    name: String(file.name || ''),
    modifiedTime: String(file.modifiedTime || ''),
    size: Number(file.size || 0),
  })).filter(file => file.id)
}

export async function uploadDriveBackup(name: string, rawJson: string): Promise<DriveBackupFile> {
  assertConfigured()
  const fileName = toBackupFile(name)
  const existing = await listByName(fileName)
  const target = existing[0] || null
  const metadata: Record<string, unknown> = target
    ? { name: fileName }
    : { name: fileName, parents: ['appDataFolder'] }
  const { boundary, body } = multipartBody(metadata, rawJson)

  const uploadBase = trimTrailingSlash(DRIVE_UPLOAD_BASE)
  const url = target
    ? `${uploadBase}/files/${encodeURIComponent(target.id)}?uploadType=multipart`
    : `${uploadBase}/files?uploadType=multipart`
  const method = target ? 'PATCH' : 'POST'
  const response = await driveFetch(url, {
    method,
    headers: {
      'Content-Type': `multipart/related; boundary=${boundary}`,
    },
    body,
  })
  if (!response.ok) {
    throw new Error(`Drive upload failed (${response.status})`)
  }

  const payload = await response.json() as Record<string, unknown>
  return {
    id: String(payload.id || target?.id || ''),
    name: String(payload.name || fileName),
    modifiedTime: String(payload.modifiedTime || new Date().toISOString()),
    size: Number(payload.size || rawJson.length),
  }
}

export async function downloadDriveBackup(fileId: string): Promise<string> {
  assertConfigured()
  const id = String(fileId || '').trim()
  if (!id) throw new Error('fileId is required')
  const url = `${DRIVE_API_BASE}/files/${encodeURIComponent(id)}?alt=media`
  const response = await driveFetch(url)
  if (!response.ok) {
    throw new Error(`Drive download failed (${response.status})`)
  }
  return response.text()
}

// --- Auto-sync scheduling ---

export type AutoSyncConflictStrategy = 'local-wins' | 'remote-wins' | 'newest-wins'

export type AutoSyncConfig = {
  enabled: boolean
  intervalMs: number
  conflictStrategy: AutoSyncConflictStrategy
}

type AutoSyncState = {
  timer: ReturnType<typeof setInterval> | null
  running: boolean
  lastSyncMs: number
  getSaveData: (() => { name: string; json: string; savedAt: number } | null) | null
  onConflict: ((remote: DriveBackupFile, local: { name: string; savedAt: number }) => 'upload' | 'skip') | null
}

const AUTOSYNC_STORAGE_KEY = 'am2_gdrive_autosync'

const sync: AutoSyncState = {
  timer: null,
  running: false,
  lastSyncMs: 0,
  getSaveData: null,
  onConflict: null,
}

export function readAutoSyncConfig(): AutoSyncConfig {
  try {
    const raw = localStorage.getItem(AUTOSYNC_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return {
        enabled: parsed.enabled === true,
        intervalMs: Math.max(30_000, Number(parsed.intervalMs) || 300_000),
        conflictStrategy: ['local-wins', 'remote-wins', 'newest-wins'].includes(parsed.conflictStrategy) ? parsed.conflictStrategy : 'newest-wins',
      }
    }
  } catch { /* ignored */ }
  return { enabled: false, intervalMs: 300_000, conflictStrategy: 'newest-wins' }
}

export function saveAutoSyncConfig(config: AutoSyncConfig): void {
  try {
    localStorage.setItem(AUTOSYNC_STORAGE_KEY, JSON.stringify(config))
  } catch { /* ignored */ }
}

function resolveConflict(strategy: AutoSyncConflictStrategy, remoteModified: string, localSavedAt: number): 'upload' | 'skip' {
  if (strategy === 'local-wins') return 'upload'
  if (strategy === 'remote-wins') return 'skip'
  const remoteMs = new Date(remoteModified).getTime()
  return localSavedAt >= remoteMs ? 'upload' : 'skip'
}

async function runAutoSyncCycle(): Promise<void> {
  if (sync.running || !hasValidToken() || !sync.getSaveData) return
  sync.running = true
  try {
    const data = sync.getSaveData()
    if (!data) return
    const config = readAutoSyncConfig()
    const fileName = toBackupFile(data.name)
    const existing = await listByName(fileName)
    const remote = existing[0] || null

    let action: 'upload' | 'skip' = 'upload'
    if (remote) {
      if (sync.onConflict) {
        action = sync.onConflict(remote, { name: data.name, savedAt: data.savedAt })
      } else {
        action = resolveConflict(config.conflictStrategy, remote.modifiedTime, data.savedAt)
      }
    }

    if (action === 'upload') {
      await uploadDriveBackup(data.name, data.json)
      console.debug('[GDrive AutoSync] Uploaded:', data.name)
    } else {
      console.debug('[GDrive AutoSync] Skipped (remote newer):', data.name)
    }
    sync.lastSyncMs = Date.now()
  } catch (err) {
    console.warn('[GDrive AutoSync] Cycle failed:', err)
  } finally {
    sync.running = false
  }
}

export function startAutoSync(
  getSaveData: () => { name: string; json: string; savedAt: number } | null,
  onConflict?: (remote: DriveBackupFile, local: { name: string; savedAt: number }) => 'upload' | 'skip',
): void {
  stopAutoSync()
  const config = readAutoSyncConfig()
  if (!config.enabled || !isDriveSyncConfigured()) return

  sync.getSaveData = getSaveData
  sync.onConflict = onConflict ?? null
  sync.timer = setInterval(() => { void runAutoSyncCycle() }, config.intervalMs)
  console.debug(`[GDrive AutoSync] Started — interval ${config.intervalMs}ms, strategy ${config.conflictStrategy}`)
}

export function stopAutoSync(): void {
  if (sync.timer) {
    clearInterval(sync.timer)
    sync.timer = null
  }
  sync.getSaveData = null
  sync.onConflict = null
}

export function triggerAutoSyncNow(): void {
  void runAutoSyncCycle()
}

export function getAutoSyncStatus(): { running: boolean; lastSyncMs: number; enabled: boolean } {
  const config = readAutoSyncConfig()
  return { running: sync.running, lastSyncMs: sync.lastSyncMs, enabled: config.enabled }
}
